import { styles } from '../../../../styles/tailwind-config'
import useNav from '../../../../hooks/useNav'
import { useEffect, useMemo, useState } from 'react'
import { useBoundStore } from '../../../../zustand'
import { InfoIcon, MapPinIcon } from 'lucide-react'
import { InfoRow } from '../../(components)/info-row'
import { StartEndHomeDrive } from '../../(components)/start-end-homeride'
import { useTimer } from '../../../../context/TimerContext'
import { InterventionPicker } from '../../(components)/intervention-picker'
import { format } from 'date-fns';
import React from 'react';
import { useAuth } from '../../../../context/AuthProvider';

function toInt(s?: string, fallback = 0) {
    const n = Number(s);
    return Number.isFinite(n) ? n : fallback;
}

const STORAGE_PREFIX = 'homeRideDone';
function makeKey(dbId?: number | string, technicianId?: number | string, yyyymmdd?: string) {
    return `${STORAGE_PREFIX}:${dbId ?? 0}:${technicianId ?? 0}:${yyyymmdd ?? ''}`;
}
function useHomeRideDoneToday() {
    const { auth } = useAuth();
    const dbId = auth.data?.databases?.[0]?.dbid;
    const technicianId = auth.data?.databases?.[0]?.technicianid;
    const today = format(new Date(), 'yyyyMMdd');

    const read = () => {
        if (!dbId || !technicianId) return false;
        try { return localStorage.getItem(makeKey(dbId, technicianId, today)) === '1'; }
        catch { return false; }
    };

    const [done, setDone] = React.useState(read);

    React.useEffect(() => {
        const onDone = () => setDone(true);
        window.addEventListener('homeRideDone', onDone as EventListener);
        return () => window.removeEventListener('homeRideDone', onDone as EventListener);
    }, []);

    React.useEffect(() => {
        try {
            const cutoff = Number(format(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), 'yyyyMMdd'));
            const prefix = `${STORAGE_PREFIX}:`;
            const rm: string[] = [];
            for (let i = 0; i < localStorage.length; i++) {
                const k = localStorage.key(i);
                if (!k || !k.startsWith(prefix)) continue;
                const dd = Number(k.split(':').pop() || '');
                if (!Number.isFinite(dd) || dd < cutoff) rm.push(k);
            }
            rm.forEach(k => localStorage.removeItem(k));
        } catch { /* ignore */ }
    }, []);

    return done;
}

const Main = () => {
    const { setNav, nav } = useNav()
    useEffect(() => {
        setNav(prev => ({ ...prev, title: 'Thuisrit' }));
    }, [setNav]);

    function findLast<T>(arr: T[] | undefined | null, pred: (x: T) => boolean): T | undefined {
        if (!Array.isArray(arr)) return undefined;
        for (let i = arr.length - 1; i >= 0; i--) if (pred(arr[i])) return arr[i];
        return undefined;
    }

    const interventionsRaw = useBoundStore((state) => state.interventions)
    const interventions = Array.isArray(interventionsRaw)
        ? interventionsRaw
        : (interventionsRaw && typeof interventionsRaw === 'object' && !Array.isArray(interventionsRaw))
            ? Object.values(interventionsRaw as Record<string, unknown>) as any[]
            : [];
    const filteredInterventions = interventions.filter(f => f.m_iSTATUS === 9)
    const paramsRaw = useBoundStore((state) => state.parameters)
    const params = Array.isArray(paramsRaw)
        ? paramsRaw
        : (paramsRaw && typeof paramsRaw === 'object' && !Array.isArray(paramsRaw))
            ? Object.values(paramsRaw as Record<string, unknown>) as any[]
            : [];
    const landParams = params.filter(f => f.m_iTYPE === 4) // type 4 === countries


    // Timer info
    const { id, isRunning, setId } = useTimer('home')
    const timerInterventionId = useMemo(() => {
        const n = toInt(id, 0);
        return n > 0 ? n : undefined;
    }, [id])

    // Defaults: last done
    const lastDone = useMemo(
        () => findLast(interventions, (i: any) => i.m_iSTATUS === 9)?.m_iINTERVENTIESID,
        [interventions]
    );

    // Local selection (user-chosen)
    const [pickerOpen, setPickerOpen] = useState(false)
    const [selectedId, setSelectedId] = useState<number | undefined>(undefined)


    // Initialize selection: prefer timer id, else last done; do it once or when timer id appears
    useEffect(() => {
        if (timerInterventionId != null) {
            setSelectedId(timerInterventionId)
            return
        }
        // only set from lastDone if we have no selection yet
        if (selectedId == null && lastDone != null) {
            setSelectedId(lastDone)
        }
    }, [timerInterventionId, lastDone, selectedId])

    // Effective id to display/use
    const effectiveInterventionId = timerInterventionId ?? selectedId ?? lastDone

    const intervention = useMemo(
        () => interventions?.find((i) => i.m_iINTERVENTIESID === effectiveInterventionId),
        [interventions, effectiveInterventionId]
    )

    const address = useMemo(() => {
        return [intervention?.m_oKLANT?.m_sADRES1, intervention?.m_oKLANT?.m_sNR]
            .map(s => s?.trim())
            .filter(Boolean)
            .join(' ')
    }, [intervention])

    const postcode = useMemo(() => {
        return [intervention?.m_oKLANT?.m_sPOSTCODE, intervention?.m_oKLANT?.m_sSTAD]
            .map(s => s?.trim())
            .filter(Boolean)
            .join(' ')
    }, [intervention])

    const land = useMemo(() => {
        const code = intervention?.m_oKLANT?.m_sKOLAN
        if (!code) return undefined
        return landParams.find((f: any) => f.m_sCODE === code)?.m_sOMS ?? code
    }, [intervention, landParams])

    const applyIntervention = (id: number) => {
        setSelectedId(id)
        if (isRunning) setId(String(id))
        setPickerOpen(false)
    }

    const doneToday = useHomeRideDoneToday();
    const disabledFinal = (filteredInterventions.length === 0) || doneToday;

    return (
        <div className='p-4 flex flex-col gap-4'>
            <h1 className={styles.h1}>
                Laatste uitgevoerde interventie
                {intervention?.m_oKLANT?.m_sEXTNUMMER?.trim()
                    ? ` - ${intervention.m_oKLANT.m_sEXTNUMMER}`
                    : ""}
            </h1>

            <InfoRow icon={<MapPinIcon className="h-5 w-5" />}>
                {intervention?.m_oKLANT?.m_sNAAM ?? (
                    <span className="text-muted-foreground">Geen naam</span>
                )}
            </InfoRow>

            <InfoRow>{address || <span className="text-muted-foreground">Geen adres</span>}</InfoRow>
            <InfoRow>{postcode || <span className="text-muted-foreground">Geen postcode</span>}</InfoRow>
            <InfoRow>{land || <span className="text-muted-foreground">Geen land</span>}</InfoRow>

            <InfoRow icon={<InfoIcon className="h-5 w-5" />}>
                {intervention?.m_sOMSCHRIJVING ?? (
                    <span className="text-muted-foreground">Geen omschrijving</span>
                )}
            </InfoRow>

            <InterventionPicker
                open={pickerOpen}
                onOpenChange={setPickerOpen}
                interventions={filteredInterventions}
                selectedId={effectiveInterventionId}
                onSelect={(id) => {
                    applyIntervention(id);
                    setPickerOpen(false);
                }}
            />

            <StartEndHomeDrive
                interventionId={intervention?.m_iINTERVENTIESID}
                disabled={disabledFinal}
            />


        </div>


    )
}

export default Main